import {
  type CampaignDecisivePointCreateDto,
  type CampaignDecisivePointSelectDto,
  type CampaignDecisivePointUpdateWithChildrenDto,
  CampaignLevelOfWarfare,
  type CampaignScenarioCreateWithVignettesDto,
  type CampaignScenarioSelectDto,
  type CampaignScenarioUpdateWithVignettesDto,
  type CampaignSelectDto,
  CampaignStatus,
  type CampaignUpdateDto,
  type CampaignVignetteCreateDto,
  type CampaignVignetteMissionRefDto,
  type CampaignVignetteSelectDto,
  type CampaignVignetteUpdateDto,
  ClassificationLevel,
  CoalitionPartner,
  KillChainCategory,
  LeadCommand,
  OperationalRegion,
} from '@@types/api.types';
import {
  COALITION_PARTNER_LABELS,
  LEAD_COMMAND_LABELS,
  OPERATIONAL_REGION_LABELS,
} from '@azymmetric/shared/types/meia/backend-persistence.types';

export enum CampaignModalStepKey {
  CONSUMED_STUDIES = 3,
  DECISIVE_POINTS = 1,
  DETAILS = 0,
  SCENARIOS = 2,
}

export const CAMPAIGN_MODAL_STEP_LABELS: Record<CampaignModalStepKey, string> = {
  [CampaignModalStepKey.CONSUMED_STUDIES]: 'Consumed Studies',
  [CampaignModalStepKey.DECISIVE_POINTS]: 'Decisive Points',
  [CampaignModalStepKey.DETAILS]: 'Details',
  [CampaignModalStepKey.SCENARIOS]: 'Scenarios',
};

export type DecisivePointDraft = {
  clientId: string;
  description: string;
  evidence: string[];
  id?: string;
  location: string;
  position: number;
};

export type VignetteMissionRefDraft = CampaignVignetteMissionRefDto;

export type VignetteDraft = {
  clientId: string;
  description: string;
  id?: string;
  missions: VignetteMissionRefDraft[];
  name: string;
  operationalRegionsOverride: OperationalRegion[];
  operationalRegionsOverrideRationale: string;
};

export type ScenarioDraft = {
  classificationLevel: ClassificationLevel;
  clientId: string;
  description: string;
  id?: string;
  levelOfWarfare: CampaignLevelOfWarfare;
  levelOfWarfareOverrideRationale: string;
  name: string;
  operationalRegions: OperationalRegion[];
  vignettes: VignetteDraft[];
};

export type CampaignDraft = {
  classificationLevel: ClassificationLevel;
  coalitionPartners: CoalitionPartner[];
  consumedStudyIds: string[];
  decisivePoints: DecisivePointDraft[];
  description: string;
  killChainCategory: KillChainCategory | null;
  leadCommands: LeadCommand[];
  name: string;
  scenarios: ScenarioDraft[];
  status: CampaignStatus;
  timeWindow: string;
};

export const createClientId = (prefix: string): string => `${prefix}-${crypto.randomUUID()}`;

export const emptyDecisivePointDraft = (): DecisivePointDraft => ({
  clientId: createClientId('dp'),
  description: '',
  evidence: [],
  location: '',
  position: 1,
});

export const emptyVignetteDraft = (): VignetteDraft => ({
  clientId: createClientId('vig'),
  description: '',
  missions: [],
  name: '',
  operationalRegionsOverride: [],
  operationalRegionsOverrideRationale: '',
});

export const emptyScenarioDraft = (defaults: { classificationLevel: ClassificationLevel }): ScenarioDraft => ({
  classificationLevel: defaults.classificationLevel,
  clientId: createClientId('scn'),
  description: '',
  levelOfWarfare: CampaignLevelOfWarfare.OPERATIONAL,
  levelOfWarfareOverrideRationale: '',
  name: '',
  operationalRegions: [],
  vignettes: [],
});

export const emptyCampaignDraft = (): CampaignDraft => ({
  classificationLevel: ClassificationLevel.UNCLASSIFIED,
  coalitionPartners: [],
  consumedStudyIds: [],
  decisivePoints: [],
  description: '',
  killChainCategory: null,
  leadCommands: [],
  name: '',
  scenarios: [],
  status: CampaignStatus.DRAFT,
  timeWindow: '',
});

export const campaignToDraft = (campaign: CampaignSelectDto): CampaignDraft => ({
  classificationLevel: campaign.classificationLevel,
  coalitionPartners: campaign.coalitionPartners,
  consumedStudyIds: campaign.consumedStudies.map((link) => link.studyId),
  decisivePoints: campaign.decisivePoints.map(decisivePointToDraft),
  description: campaign.description ?? '',
  killChainCategory: campaign.killChainCategory ?? null,
  leadCommands: campaign.leadCommands,
  name: campaign.name,
  scenarios: campaign.scenarios.map(scenarioToDraft),
  status: campaign.status,
  timeWindow: campaign.timeWindow ?? '',
});

const decisivePointToDraft = (point: CampaignDecisivePointSelectDto): DecisivePointDraft => ({
  clientId: point.id,
  description: point.description,
  evidence: point.evidence,
  id: point.id,
  location: point.location ?? '',
  position: point.position + 1,
});

const vignetteToDraft = (vignette: CampaignVignetteSelectDto): VignetteDraft => ({
  clientId: vignette.id,
  description: vignette.description ?? '',
  id: vignette.id,
  missions: vignette.missions,
  name: vignette.name,
  operationalRegionsOverride: vignette.operationalRegionsOverride,
  operationalRegionsOverrideRationale: vignette.operationalRegionsOverrideRationale ?? '',
});

const scenarioToDraft = (scenario: CampaignScenarioSelectDto): ScenarioDraft => ({
  classificationLevel: scenario.classificationLevel,
  clientId: scenario.id,
  description: scenario.description ?? '',
  id: scenario.id,
  levelOfWarfare: scenario.levelOfWarfare,
  levelOfWarfareOverrideRationale: scenario.levelOfWarfareOverrideRationale ?? '',
  name: scenario.name,
  operationalRegions: scenario.operationalRegions,
  vignettes: scenario.vignettes.map(vignetteToDraft),
});

export const decisivePointDraftToCreateDto = (draft: DecisivePointDraft): CampaignDecisivePointCreateDto => ({
  description: draft.description.trim(),
  evidence: draft.evidence.filter((entry) => entry.trim().length > 0),
  location: draft.location.trim() || null,
  position: Math.max(0, draft.position - 1),
});

export const vignetteDraftToCreateDto = (draft: VignetteDraft): CampaignVignetteCreateDto => ({
  description: draft.description.trim() || null,
  missionIds: draft.missions.map((mission) => mission.id),
  name: draft.name.trim(),
  operationalRegionsOverride: draft.operationalRegionsOverride,
  operationalRegionsOverrideRationale: draft.operationalRegionsOverrideRationale.trim() || null,
});

export const scenarioDraftToCreateDto = (draft: ScenarioDraft): CampaignScenarioCreateWithVignettesDto => ({
  classificationLevel: draft.classificationLevel,
  description: draft.description.trim() || null,
  levelOfWarfare: draft.levelOfWarfare,
  levelOfWarfareOverrideRationale: draft.levelOfWarfareOverrideRationale.trim() || null,
  name: draft.name.trim(),
  operationalRegions: draft.operationalRegions,
  vignettes: draft.vignettes.map(vignetteDraftToCreateDto),
});

const vignetteDraftToUpdateDto = (draft: VignetteDraft): CampaignVignetteUpdateDto => ({
  description: draft.description.trim() || null,
  id: draft.id,
  missionIds: draft.missions.map((mission) => mission.id),
  name: draft.name.trim(),
  operationalRegionsOverride: draft.operationalRegionsOverride,
  operationalRegionsOverrideRationale:
    draft.operationalRegionsOverride.length === 0 ? null : draft.operationalRegionsOverrideRationale.trim() || null,
});

const scenarioDraftToUpdateDto = (draft: ScenarioDraft): CampaignScenarioUpdateWithVignettesDto => ({
  classificationLevel: draft.classificationLevel,
  description: draft.description.trim() || null,
  id: draft.id,
  levelOfWarfare: draft.levelOfWarfare,
  levelOfWarfareOverrideRationale:
    draft.levelOfWarfare === CampaignLevelOfWarfare.OPERATIONAL
      ? null
      : draft.levelOfWarfareOverrideRationale.trim() || null,
  name: draft.name.trim(),
  operationalRegions: draft.operationalRegions,
  vignettes: draft.vignettes.map(vignetteDraftToUpdateDto),
});

const decisivePointDraftToUpdateDto = (draft: DecisivePointDraft): CampaignDecisivePointUpdateWithChildrenDto => ({
  description: draft.description.trim(),
  evidence: draft.evidence.filter((entry) => entry.trim().length > 0),
  id: draft.id,
  location: draft.location.trim() || null,
  position: Math.max(0, draft.position - 1),
});

export const campaignDraftToUpdateDto = (draft: CampaignDraft): CampaignUpdateDto => ({
  classificationLevel: draft.classificationLevel,
  coalitionPartners: draft.coalitionPartners,
  consumedStudyIds: draft.consumedStudyIds,
  decisivePoints: draft.decisivePoints.map(decisivePointDraftToUpdateDto),
  description: draft.description.trim() || null,
  killChainCategory: draft.killChainCategory,
  leadCommands: draft.leadCommands,
  name: draft.name.trim(),
  scenarios: draft.scenarios.map(scenarioDraftToUpdateDto),
  status: draft.status,
  timeWindow: draft.timeWindow.trim() || null,
});

export const KILL_CHAIN_CATEGORY_LABELS: Record<KillChainCategory, string> = {
  [KillChainCategory.C2]: 'Command & Control',
  [KillChainCategory.COUNTER_UAS]: 'Counter-UAS',
  [KillChainCategory.CUSTOM]: 'Custom',
  [KillChainCategory.CYBER]: 'Cyber',
  [KillChainCategory.D3A]: 'D3A',
  [KillChainCategory.F2T2EA]: 'F2T2EA',
  [KillChainCategory.ISR]: 'ISR',
  [KillChainCategory.LOGISTICS]: 'Logistics',
};

export const CLASSIFICATION_LABELS: Record<ClassificationLevel, string> = {
  [ClassificationLevel.UNCLASSIFIED]: 'Unclassified',
  [ClassificationLevel.CUI]: 'CUI',
  [ClassificationLevel.SECRET]: 'Secret',
  [ClassificationLevel.TOP_SECRET]: 'Top Secret',
};

export const CAMPAIGN_STATUS_LABELS: Record<CampaignStatus, string> = {
  [CampaignStatus.ACTIVE]: 'Active',
  [CampaignStatus.ARCHIVED]: 'Archived',
  [CampaignStatus.DRAFT]: 'Draft',
  [CampaignStatus.HISTORICAL]: 'Historical',
};

export const LEVEL_OF_WARFARE_LABELS: Record<CampaignLevelOfWarfare, string> = {
  [CampaignLevelOfWarfare.OPERATIONAL]: 'Operational',
  [CampaignLevelOfWarfare.STRATEGIC]: 'Strategic',
  [CampaignLevelOfWarfare.TACTICAL]: 'Tactical',
};

const buildOptions = <T extends string>(labels: Record<T, string>): Array<{ label: string; value: T }> =>
  (Object.keys(labels) as T[]).map((value) => ({ label: labels[value], value }));

export const CLASSIFICATION_OPTIONS = [
  { label: 'Unclassified', value: ClassificationLevel.UNCLASSIFIED },
  { label: 'CUI', value: ClassificationLevel.CUI },
  { label: 'Secret', value: ClassificationLevel.SECRET },
  { label: 'Top Secret', value: ClassificationLevel.TOP_SECRET },
];

export const STATUS_OPTIONS = [
  { label: 'Draft', value: CampaignStatus.DRAFT },
  { label: 'Active', value: CampaignStatus.ACTIVE },
  { label: 'Historical', value: CampaignStatus.HISTORICAL },
  { label: 'Archived', value: CampaignStatus.ARCHIVED },
];

export const LEVEL_OF_WARFARE_OPTIONS = [
  { label: 'Tactical', value: CampaignLevelOfWarfare.TACTICAL },
  { label: 'Operational', value: CampaignLevelOfWarfare.OPERATIONAL },
  { label: 'Strategic', value: CampaignLevelOfWarfare.STRATEGIC },
];

// SelectField stores `position: number` from the field context — the Select
// option `value: string` is converted back to number at the boundary
// (apps/web/src/components/ui/form/select-field.component.tsx handleValueChange).
export const DECISIVE_POINT_RANK_OPTIONS = [1, 2, 3, 4, 5].map((rank) => ({
  label: rank.toString(),
  value: rank.toString(),
}));

export const KILL_CHAIN_CATEGORY_OPTIONS = buildOptions(KILL_CHAIN_CATEGORY_LABELS);
export const LEAD_COMMAND_OPTIONS = buildOptions(LEAD_COMMAND_LABELS);
export const COALITION_PARTNER_OPTIONS = buildOptions(COALITION_PARTNER_LABELS);
export const OPERATIONAL_REGION_OPTIONS = buildOptions(OPERATIONAL_REGION_LABELS);

export { COALITION_PARTNER_LABELS, LEAD_COMMAND_LABELS, OPERATIONAL_REGION_LABELS };
